import React, { useState, useCallback, useEffect, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '../../AppContext';
import * as LucideIcons from 'lucide-react';
import { triggerHaptic } from '../../utils/haptics';

import ReactMarkdown from 'react-markdown';

function DreamWeaveAIChat({ dreamWeaveId, nexusId }) {
    const { t } = useTranslation();
    const { appFunctions, userId, user } = useAppContext();
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [generateImage, setGenerateImage] = useState(false);
    const chatContainerRef = useRef(null);
    const formRef = useRef(null);

    const quickReplies = [
        t('dreamWeaveAIChat.quickReplies.purpose'),
        t('dreamWeaveAIChat.quickReplies.influentialThought'),
        t('dreamWeaveAIChat.quickReplies.visualizeTheme')
    ];

    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [messages]);

    const handleSendMessage = useCallback(async (e) => {
        if (e) e.preventDefault();
        if (!input.trim() || loading) return;
        if (!userId || !user || user.isAnonymous) {
            setError(t('dreamWeaveAIChat.signInError'));
            return;
        }
        
        triggerHaptic('medium');
        const userMessage = { id: Date.now(), sender: 'user', text: input };
        setMessages(prev => [...prev, userMessage]);
        setInput('');
        setLoading(true);
        setError(null);

        const history = messages.slice(-5).map(msg => ({
            role: msg.sender === 'user' ? 'user' : 'model',
            parts: [{ text: msg.text }],
        }));

        try {
            const generateContributions = httpsCallable(appFunctions, 'generateAiContributions');
            const result = await generateContributions({
                dreamWeaveId: dreamWeaveId,
                nexusId: nexusId,
                message: input,
                history: history,
                generateImage: generateImage,
            });

            if (result.data.success) {
                triggerHaptic('success');
                const aiResponse = { 
                    id: Date.now() + 1, 
                    sender: 'ai', 
                    text: result.data.response,
                    imageUrl: result.data.imageUrl,
                    isContribution: result.data.contributionsAdded > 0,
                    contributionCount: result.data.contributionsAdded,
                };
                setMessages(prev => [...prev, aiResponse]);
            } else {
                throw new Error(result.data.message || t('dreamWeaveAIChat.contributionError'));
            }
        } catch (err) {
            triggerHaptic('error');
            console.error("Error generating AI contributions:", err);
            setError(t('dreamWeaveAIChat.genericError', { message: err.message }));
            setMessages(prev => [...prev, { id: Date.now() + 1, sender: 'ai', text: t('dreamWeaveAIChat.unexpectedError') }]);
        } finally {
            setLoading(false);
            setGenerateImage(false); // Reset after every message
        }
    }, [input, loading, userId, user, dreamWeaveId, nexusId, appFunctions, messages, generateImage, t]);

    const handleQuickReply = (reply) => {
        setInput(reply);
        if (reply.toLowerCase().includes('visualize') || reply.toLowerCase().includes('visualizar')) {
            setGenerateImage(true);
        }
        setTimeout(() => formRef.current.requestSubmit(), 0);
    };

    const QuickReplyButton = ({ text }) => (
        <button
            onClick={() => handleQuickReply(text)}
            className="px-3 py-1.5 bg-gray-700/50 text-indigo-300 text-xs rounded-full hover:bg-gray-700 transition-colors"
        >
            {text}
        </button>
    );

    if (!dreamWeaveId) {
        return <div className="text-white text-center p-4">{t('dreamWeaveAIChat.selectPrompt')}</div>;
    }

    const cost = generateImage ? 50 : 15;

    return (
        <div className="flex flex-col h-full p-4 bg-gray-800 rounded-lg">
            <div ref={chatContainerRef} className="flex-grow overflow-y-auto custom-scrollbar p-2 space-y-4">
                {messages.length === 0 && !loading && (
                    <div className="flex flex-col items-center justify-center h-full text-center text-gray-500">
                        <LucideIcons.Bot size={48} className="mb-4" />
                        <h3 className="text-lg font-semibold text-gray-400">{t('dreamWeaveAIChat.welcomeTitle')}</h3>
                        <p className="text-sm">{t('dreamWeaveAIChat.welcomeSubtitle')}</p>
                        <p className="text-xs mt-2">{t('dreamWeaveAIChat.costInfo')}</p>
                    </div>
                )}
                {messages.slice(-20).map((msg) => (
                    <div
                        key={msg.id}
                        className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                        <div
                            className={`prose prose-invert max-w-[70%] p-3 rounded-lg shadow-md ${msg.sender === 'user'
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-700 text-gray-100'
                            }`}
                        >
                            {msg.imageUrl && (
                                <div className="mb-2 border-b border-gray-600 pb-2">
                                    <img src={msg.imageUrl} alt={t('dreamWeaveAIChat.visualizationAlt')} className="rounded-lg w-full h-auto" />
                                </div>
                            )}
                            <ReactMarkdown>{msg.text}</ReactMarkdown>
                            {msg.isContribution && (
                                <p className="text-xs mt-2 pt-2 border-t border-gray-600 text-cyan-300">
                                    {t('dreamWeaveAIChat.manifestedMessage', { count: msg.contributionCount })}
                                </p>
                            )}
                        </div>
                    </div>
                ))}
                {loading && (
                    <div className="flex justify-start">
                        <div className="max-w-[70%] p-3 rounded-lg shadow-md bg-gray-700 text-gray-100 flex flex-col items-center space-y-2">
                            {generateImage && <div className="w-48 h-32 bg-gray-600 rounded-lg animate-pulse"></div>}
                            <div className="flex items-center space-x-2">
                                <LucideIcons.Loader size={18} className="animate-spin text-blue-300" />
                                <span>{t('dreamWeaveAIChat.generating')}</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            {messages.length === 0 && !loading && (
                <div className="flex-shrink-0 py-2 flex items-center justify-center gap-2 flex-wrap">
                    {quickReplies.map(reply => <QuickReplyButton key={reply} text={reply} />)}
                </div>
            )}
            {error && <p className="text-red-400 text-sm mt-2">{error}</p>}
            <form ref={formRef} onSubmit={handleSendMessage} className="flex mt-4 space-x-2">
                <button
                    type="button"
                    onClick={() => setGenerateImage(!generateImage)}
                    className={`p-3 rounded-lg font-bold transition duration-200 ${generateImage ? 'bg-purple-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
                    aria-label={t('dreamWeaveAIChat.toggleImageAriaLabel')}
                >
                    <LucideIcons.Image size={20} />
                </button>
                <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder={loading ? t('dreamWeaveAIChat.placeholderGenerating') : t('dreamWeaveAIChat.placeholder', { cost })}
                    className={`flex-grow p-3 rounded-lg text-white border focus:outline-none ${loading ? 'bg-gray-600 border-gray-500 cursor-not-allowed' : 'bg-gray-700 border-gray-600 focus:border-blue-500'}`}
                    disabled={loading}
                />
                <button
                    type="submit"
                    className="bg-indigo-600 hover:bg-indigo-700 text-white p-3 rounded-lg font-bold transition duration-200 disabled:bg-gray-500"
                    disabled={loading || !input.trim()}
                >
                    <LucideIcons.Send size={20} />
                </button>
            </form>
        </div>
    );
}

export default DreamWeaveAIChat;